import { Component, OnInit, Input, ViewChild, ViewChildren, QueryList, OnDestroy, Output, EventEmitter, AfterViewInit, ElementRef } from '@angular/core';
import { ScoutPath, ScoutPoint, ScoutPointRefData, ScoutPointPhoto, ScoutTask } from '../../../core/models';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { NgForm } from '@angular/forms';
import { ScoutPointPhotoEditorComponent } from '../scout-point-photo-editor/scout-point-photo-editor.component';
import { ScoutTaskService, ScoutPointPhotoService } from '../../../core/services';
import { Lightbox, LightboxConfig } from 'ngx-lightbox';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import { AppState } from '../../../core';
import { ScoutPointPhotoDoc } from '../../../core/schema/scout-point-photo-schema';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

const groupBy = (xs, f) => {
  return xs.reduce((r, v, i, a, k = f(v)) => ((r[k] || (r[k] = [])).push(v), r), {});
}

@Component({
  selector: 'app-scout-point-ref-measurements',
  templateUrl: './scout-point-ref-measurements.component.html',
  styleUrls: ['./scout-point-ref-measurements.component.scss']
})
export class ScoutPointRefMeasurementsComponent extends FesBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() showTitlebar = false
  @Input() isEditing = false
  @Input() locked : boolean
  @Input() scoutTaskDoc : ScoutTaskDoc
  @Input() scoutPath : ScoutPath
  @Input() set scoutPoint( sp : ScoutPoint ) {
    console.log('set scoutPoint() id='+sp.id)
    this._scoutPoint = sp
    this.photoDocs = []
    this.initLocations()
    this.initPhotosForPointQuery()
  }
  @Input() hidePhotos? : boolean = false

  @ViewChild('refMeasurementsForm') refMeasurementsForm: NgForm;
  @ViewChildren(ScoutPointPhotoEditorComponent) photoEditors!: QueryList<ScoutPointPhotoEditorComponent>;

  @Output('photoAdded') photoAdded = new EventEmitter<ScoutPointPhotoDoc>()
  @Output('photoSaved') photoSavedEmitter = new EventEmitter<ScoutPointPhotoDoc>()
  @Output('editChange') editChange = new EventEmitter<NgForm>()
  @Output() editModeChanged = new EventEmitter<ScoutPointRefMeasurementsComponent>()

  _scoutPoint : ScoutPoint
  get scoutPoint() { return this._scoutPoint }
  photoDocs : ScoutPointPhotoDoc[] = []
  isLoadingPhotos = false
  selectedPhoto = new BehaviorSubject<ScoutPointPhotoDoc>(null)

  constructor(
      appState: AppState, 
      protected scoutTaskService: ScoutTaskService, 
      protected photoService: ScoutPointPhotoService,
      public elementRef: ElementRef,
      private lightbox: Lightbox,
      lightboxConfig: LightboxConfig) {
    super(appState)
    lightboxConfig.fitImageInViewPort = true
    lightboxConfig.showRotate = true
    lightboxConfig.showZoom = true
    //lightboxConfig.disableScrolling = false // this is for elements below lightbox and not the image
    lightboxConfig.fadeDuration = 0.4
   }


  ngOnInit(): void {
    this.initMediaDevices()
    if ( this.scoutPoint && !this.scoutPoint.refData ) {
      this.scoutPoint.refData = new ScoutPointRefData()
    }
  }

  ngAfterViewInit(): void {
    this.subscribe(this.refMeasurementsForm.statusChanges, status => this.editChange.emit(this.refMeasurementsForm))
    this.subscribe(this.refMeasurementsForm.valueChanges, change => this.editChange.emit(this.refMeasurementsForm))
  }

  ngOnDestroy(): void {
    this.destroy()
  }


  initLocations() {
    console.log('initLocations()')
    let refData = this._scoutPoint.refData
    if ( !refData ) {
      console.warn('missing refData... constructing default ref data')
      refData = this._scoutPoint.refData = new ScoutPointRefData()
    }
    if ( refData.location1Distance === undefined ) {
      refData.location1Distance = 0
    }
    if ( refData.location2Distance === undefined ) {
      refData.location2Distance = 0
    }     
  }

  
  async initMediaDevices() {
    console.log('initMediaDevices()')
    if ( !this.photoService.compatibleCameras?.length ) {      
      await this.photoService.updateCompatibleCameras()
    }
  }

  initializedSubject = new BehaviorSubject<boolean>(false)

  initPhotosForPointQuery = async () => {
    this.unsubscribe()
    this.isLoadingPhotos = true
    let photoQuery = this.photoService.rxCollection.find().where('scoutPointId').eq(this._scoutPoint.id)
    this.subscribe( photoQuery.$, 
      async (docs : ScoutPointPhotoDoc[]) => { 
        this.isLoadingPhotos = false
        this.initPhotosForPointType(docs)
        timer(0).subscribe(_=>this.initializedSubject.next(true))
      }, 
      error => {
        console.log('photoQuery subscription received error')
        this.isLoadingPhotos = false; this.photoService.publishError(error,this)
      })
  }


  initPhotosForPointType(photoDocs : ScoutPointPhotoDoc[]) {
    this.photoService.initPhotosForPointType(photoDocs,this.scoutPoint.type,this.photoDocs)
  }


  onNewPhoto = ( groupName : string ) => {
    console.log('onNewPhoto(' + groupName + ')')
    let photo = this.photoService.rxCollection.newDocument()
    //photo._id = uuidv4()
    photo.group = groupName
    const groupPhotos = this.photoDocs.filter(doc=>doc.group==groupName)
    //console.log(groupName + ': ' + groupPhotos.map(doc=>doc.groupOrderIndex).join(","))
    const maxOrderIndex = Math.max(0,...groupPhotos.map(doc=>doc.groupOrderIndex))
    photo.groupOrderIndex = maxOrderIndex + 1
    //console.log('new photo groupOrderIndex: ' + photo.groupOrderIndex)
    photo.pointType = this.scoutPoint.type
    photo.scoutPointId = this._scoutPoint.id
    this.photoDocs.push(photo)
    this.initPhotosForPointType(this.photoDocs)
    timer(0).subscribe(ms=>this.photoAdded.emit(photo))
    return photo
  } 
  
  
  onNewPhotoAndScrollTo = ( groupName: string) => {
    this.onNewPhoto(groupName)
    timer(0).subscribe(_=>{
      const groupElementId = groupName.replace(/^point-to-point/,'path-view') + '-photos'
      this.scrollTo(groupElementId + '-' + (this.photoDocs.length-1))
    })
  }


  onPhotoClick = ( photo : ScoutPointPhoto, photoUrl : string ) => {
    const albums = this.photoEditors.filter(editor=>editor.photoUrl!=null).map(editor=> {
      return {
        src: editor.photoUrl, 
        thumb: null, 
        caption: editor.photo?.title + ' (' + editor.photo?.fileName + ')',
        photo: editor.photo
      }
    })
    const index = albums.findIndex(album=>album.photo==photo)
    this.lightbox.open(albums,index)
  }


  onPhotoDocDeleted( photoDoc : ScoutPointPhotoDoc ) {
    console.log('onPhotoDocDeleted() title=' + photoDoc?.title )
    const index = this.photoDocs.findIndex(doc=>doc==photoDoc)    
    this.photoDocs.splice(index,1)
    this.updateGroupOrderIndices()
  }

  
  onPhotoOrderChanged( photoDoc : ScoutPointPhotoDoc ) {
    this.updateGroupOrderIndices()
  }


  onPhotoSaved(photoDoc : ScoutPointPhotoDoc ) {
    this.photoSavedEmitter.emit(photoDoc)
  }


  async updateGroupOrderIndices() {
    const sortByGroupFn = ( lhs,rhs ) => (lhs.groupOrderIndex > rhs.groupOrderIndex) ? 1 : -1;
    const groups = this.photoDocs?.groupBy( (photoDoc:ScoutPointPhotoDoc)=> photoDoc.group)
    for( let groupName in groups ) {
      let photoDocs : ScoutPointPhotoDoc[] = groups[groupName]
      photoDocs.sort(sortByGroupFn)
      photoDocs.forEach( async ( doc, index ) => {
        if ( doc.groupOrderIndex != index ) {
          try {
            console.log('updating "'+ groupName +'" order index from ' + doc?.groupOrderIndex + ' to ' + index)
            if ( !doc._id ) {
              doc.groupOrderIndex = index
            } else {
              await doc.atomicPatch({groupOrderIndex:index})
            }
          } catch ( error ) {
            console.error('Failed updating groupOrder index from ' + doc?.groupOrderIndex + ' to ' + index)
            console.error(this.photoService.extractErrorMsg(error))
          }
        }
      })
    }
  }


  onValueClicked() {
    if ( !this.isEditing ) {
      this.isEditing = true
      this.editModeChanged.emit(this)
    }
  }


  async savePhotoEdits() {
    const bReloadPhotosAfterSave = false
    const promises = this.photoEditors.map( editor => editor.saveEdits(bReloadPhotosAfterSave) )
    const photoSaveResults = await Promise.allSettled(promises)
    if ( photoSaveResults.some(result=>result.status=='rejected') ) {      
      throw new Error('Issue(s) occured during photo save.')
    }
  }

  scrollTo( elementId : string, block: ScrollLogicalPosition = 'start' ) {
    this.initializedSubject.pipe( skipWhile(bInit=>bInit==false), take(1)).subscribe(_=> {
      const scoutPointElement = /*this.elementRef.nativeElement*/document.getElementById(elementId); 
      this.scrollToElement(scoutPointElement,block)
    })
  }

  scrollToElement( scoutPointElement : any, block: ScrollLogicalPosition = 'start' ) {
    this.initializedSubject.pipe( skipWhile(bInit=>bInit==false), take(1)).subscribe(_=> {
      if (scoutPointElement) {
        scoutPointElement.scrollIntoView({behavior: "smooth", block: block });
        scoutPointElement.classList.add('scrolled-to')
        timer(1*1500).subscribe(_=>scoutPointElement.classList.remove('scrolled-to'))
      }  
    })
  }
}


